import { useMemo } from 'react';
import { ExclamationCircleIcon } from '@heroicons/react/24/outline';

export interface ValidationError {
    /** Row number in the CSV (1-indexed) */
    row: number;
    /** Field name that has the error */
    field: string;
    /** Error message */
    message: string;
}

interface ValidationErrorListProps {
    /** Array of validation errors */
    errors: ValidationError[];
    /** Optional max height in pixels (default: 300) */
    maxHeight?: number;
}

interface GroupedErrors {
    field: string;
    errors: ValidationError[];
}

/**
 * ValidationErrorList displays validation errors grouped by field type.
 * Shows row numbers prominently and is scrollable for long lists.
 */
export function ValidationErrorList({ errors, maxHeight = 300 }: ValidationErrorListProps) {
    // Group errors by field
    const groupedErrors = useMemo((): GroupedErrors[] => {
        const groups: Record<string, ValidationError[]> = {};

        errors.forEach((error) => {
            const fieldKey = error.field.toLowerCase();
            if (!groups[fieldKey]) {
                groups[fieldKey] = [];
            }
            groups[fieldKey].push(error);
        });

        // Convert to array and sort by field name
        return Object.entries(groups)
            .map(([field, fieldErrors]) => ({
                field,
                errors: fieldErrors.sort((a, b) => a.row - b.row),
            }))
            .sort((a, b) => a.field.localeCompare(b.field));
    }, [errors]);

    if (errors.length === 0) {
        return null;
    }

    // Capitalize first letter of field name
    const formatFieldName = (field: string): string => {
        return field.charAt(0).toUpperCase() + field.slice(1);
    };

    return (
        <div className="w-full">
            <div className="flex items-center gap-2 mb-3">
                <ExclamationCircleIcon className="h-5 w-5 text-red-500" />
                <h4 className="font-medium text-red-700 dark:text-red-400">
                    Validation Errors ({errors.length})
                </h4>
            </div>

            <div
                className="overflow-y-auto border border-red-200 dark:border-red-800 rounded-lg bg-red-50 dark:bg-red-900/20"
                style={{ maxHeight: `${maxHeight}px` }}
            >
                <div className="p-4 space-y-4">
                    {groupedErrors.map((group) => (
                        <div key={group.field}>
                            <h5 className="text-sm font-medium text-red-800 dark:text-red-300 mb-2 uppercase tracking-wide">
                                {formatFieldName(group.field)}
                            </h5>
                            <ul className="space-y-2">
                                {group.errors.map((error, index) => (
                                    <li
                                        key={`${error.row}-${index}`}
                                        className="flex items-start gap-2 text-sm"
                                    >
                                        <span className="inline-flex items-center justify-center min-w-12 px-2 py-0.5 bg-red-200 dark:bg-red-800 text-red-800 dark:text-red-200 rounded-sm text-xs font-medium">
                                            Row {error.row}
                                        </span>
                                        <span className="text-red-700 dark:text-red-400">
                                            {error.message}
                                        </span>
                                    </li>
                                ))}
                            </ul>
                        </div>
                    ))}
                </div>
            </div>
        </div>
    );
}
